import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  canOpenLocalPaths,
  classifyOpenableValue,
  copyTextToClipboard,
  findOpenableAtPoint,
  openableValueHint,
  openOpenableValue,
} from '../utils/openableValue';
import '../styles/openable-value-tip.css';

const OFFSET_X = 12;
const OFFSET_Y = 18;
const FEEDBACK_MS = 1200;
const COPY_MENU_MS = 700;

const isLocalPathKind = (kind) => kind === 'local-file' || kind === 'local-folder';

/**
 * Variable pill in the doc editor whose value is a filesystem path.
 * Hit-test only — skip the hover tip's style reads so the menu can paint
 * in this turn.
 */
const localPathVariableAt = (clientX, clientY) => {
  const el = document.elementFromPoint(clientX, clientY);
  if (!el?.closest?.('.doc-editor')) return null;
  if (el.closest?.('[data-doc-math]')) return null;
  const pill = el.closest?.('[data-doc-variable]');
  if (!pill) return null;
  const display = (pill.textContent || '').trim();
  if (!display) return null;
  const { kind, target } = classifyOpenableValue(display);
  if (!isLocalPathKind(kind) || !target) return null;
  return { target };
};

const feedbackLabel = (result) => {
  if (result === 'opened') return 'Opened!';
  if (result === 'failed') return "Couldn't open";
  return 'Copied!';
};

/**
 * App-wide cursor-following tip for openable / copyable values:
 * variable pills, external anchors, and plain-text URLs / paths / bucket URIs.
 */
export const useOpenableHoverTip = () => {
  const [tip, setTip] = useState(null);
  const [menu, setMenu] = useState(null);
  const tipElRef = useRef(null);
  const menuElRef = useRef(null);
  const activeIdRef = useRef(null);
  const activePayloadRef = useRef(null);
  const feedbackTimerRef = useRef(null);
  const menuOpenRef = useRef(false);
  const copyTimerRef = useRef(null);
  const copyStartedRef = useRef(false);
  const closeMenuRef = useRef(() => {});

  useEffect(() => {
    const clearFeedbackTimer = () => {
      if (feedbackTimerRef.current) {
        clearTimeout(feedbackTimerRef.current);
        feedbackTimerRef.current = null;
      }
    };

    const hide = () => {
      activeIdRef.current = null;
      activePayloadRef.current = null;
      clearFeedbackTimer();
      setTip(null);
    };

    const place = (el, clientX, clientY) => {
      if (!el) return;
      el.style.left = `${clientX + OFFSET_X}px`;
      el.style.top = `${clientY + OFFSET_Y}px`;
    };

    const isInEditor = (el) =>
      !!el?.closest?.(
        '[contenteditable="true"], [contenteditable=""], textarea, input, [role="textbox"]'
      );

    // Plain-text URLs and auto-linked <a> tags both need Ctrl/Cmd inside editors
    // so a normal click can place the caret (DocEditor auto-wraps bare URLs;
    // VariablesPanel value cells are contenteditable textboxes with path values).
    const needsEditorModClick = (hit) =>
      hit.source === 'text' || hit.source === 'anchor';

    const hintForHit = (hit, clientX, clientY) => {
      if (hit.source === 'math') return 'Click to edit /math formula';
      let hint = openableValueHint(hit.kind, { canOpenLocal: canOpenLocalPaths() });
      if (needsEditorModClick(hit)) {
        const under = document.elementFromPoint(clientX, clientY);
        if (isInEditor(under)) {
          hint = hint.replace(/click to /i, 'Ctrl+click to ');
        }
      }
      if (
        canOpenLocalPaths() &&
        hit.source === 'variable' &&
        isLocalPathKind(hit.kind) &&
        document.elementFromPoint(clientX, clientY)?.closest?.('.doc-editor')
      ) {
        hint = `${hint}. Right-click to copy`;
      }
      return hint;
    };

    const closeMenu = () => {
      menuOpenRef.current = false;
      copyStartedRef.current = false;
      if (copyTimerRef.current) {
        clearTimeout(copyTimerRef.current);
        copyTimerRef.current = null;
      }
      setMenu(null);
    };
    closeMenuRef.current = closeMenu;

    const onPointerMove = (e) => {
      if (menuOpenRef.current) return;
      const hit = findOpenableAtPoint(e.clientX, e.clientY);
      if (!hit) {
        if (activeIdRef.current) hide();
        return;
      }

      if (activeIdRef.current === hit.id) {
        place(tipElRef.current, e.clientX, e.clientY);
        activePayloadRef.current = hit;
        return;
      }

      activeIdRef.current = hit.id;
      activePayloadRef.current = hit;
      clearFeedbackTimer();
      setTip({
        x: e.clientX + OFFSET_X,
        y: e.clientY + OFFSET_Y,
        label: hit.label,
        kind: hit.kind,
        target: hit.target,
        color: hit.color,
        state: hit.state,
        hint: hintForHit(hit, e.clientX, e.clientY),
        feedback: null,
      });
    };

    const onClick = (e) => {
      if (e.target?.closest?.('.openable-value-menu')) return;
      const under = document.elementFromPoint(e.clientX, e.clientY);
      const inEditor = isInEditor(under);
      // Re-hit in case the pointer moved slightly; fall back to last hover payload.
      // Inside editors, never fall back — a near-miss would steal the caret click.
      const hit =
        findOpenableAtPoint(e.clientX, e.clientY) ||
        (!inEditor ? activePayloadRef.current : null);
      if (!hit?.target) return;

      // Don't steal clicks from chrome controls that happen to contain a path-like string
      if (
        hit.source === 'text' &&
        under?.closest?.('button, [role="button"], input, textarea, select, option')
      ) {
        return;
      }
      // In editors, require Ctrl/Cmd+click so a normal click still places the caret
      if (needsEditorModClick(hit) && inEditor && !e.ctrlKey && !e.metaKey) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      const markFeedback = (result) => {
        if (activeIdRef.current !== hit.id && activePayloadRef.current?.id !== hit.id) return;
        clearFeedbackTimer();
        setTip((prev) =>
          prev ? { ...prev, feedback: feedbackLabel(result) } : prev
        );
        feedbackTimerRef.current = setTimeout(() => {
          feedbackTimerRef.current = null;
          setTip((prev) => (prev ? { ...prev, feedback: null } : prev));
        }, FEEDBACK_MS);
      };

      openOpenableValue(hit.kind, hit.target)
        .then(markFeedback)
        .catch(() => markFeedback('failed'));
    };

    const onContextMenu = (e) => {
      const hit = localPathVariableAt(e.clientX, e.clientY);
      if (!hit) {
        if (menuOpenRef.current) closeMenu();
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      hide();
      menuOpenRef.current = true;
      copyStartedRef.current = false;
      setMenu({ x: e.clientX, y: e.clientY, target: hit.target, copied: false });
    };

    const onPointerDown = (e) => {
      if (!menuOpenRef.current) return;
      if (e.target?.closest?.('.openable-value-menu')) return;
      closeMenu();
    };

    const onKeyDown = (e) => {
      if (e.key === 'Escape' && menuOpenRef.current) closeMenu();
    };

    const onScroll = () => {
      hide();
      if (menuOpenRef.current) closeMenu();
    };

    document.addEventListener('pointermove', onPointerMove, { passive: true });
    document.addEventListener('click', onClick, true);
    document.addEventListener('contextmenu', onContextMenu, true);
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('blur', hide);

    return () => {
      clearFeedbackTimer();
      if (copyTimerRef.current) {
        clearTimeout(copyTimerRef.current);
        copyTimerRef.current = null;
      }
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('contextmenu', onContextMenu, true);
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('blur', hide);
    };
  }, []);

  useLayoutEffect(() => {
    const el = menuElRef.current;
    if (!menu || !el || menu.copied) return;
    const rect = el.getBoundingClientRect();
    let x = menu.x;
    let y = menu.y;
    if (rect.right > window.innerWidth - 8) x = Math.max(8, window.innerWidth - rect.width - 8);
    if (rect.bottom > window.innerHeight - 8) y = Math.max(8, window.innerHeight - rect.height - 8);
    if (x !== menu.x || y !== menu.y) {
      setMenu((prev) => (prev ? { ...prev, x, y } : prev));
    }
  }, [menu]);

  const handleCopy = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    // Keep the editor focused. A normal click would blur it and run a
    // document commit before the clipboard write.
    if (copyStartedRef.current) return;
    const target = menu?.target;
    if (!target) return;
    copyStartedRef.current = true;
    setMenu((prev) => (prev ? { ...prev, copied: true } : prev));
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => {
      copyTimerRef.current = null;
      closeMenuRef.current();
    }, COPY_MENU_MS);
    copyTextToClipboard(target).catch(() => {
      copyStartedRef.current = false;
      if (copyTimerRef.current) {
        clearTimeout(copyTimerRef.current);
        copyTimerRef.current = null;
      }
      setMenu((prev) => (prev ? { ...prev, copied: false } : prev));
    });
  };

  useEffect(() => {
    if (!tip) tipElRef.current = null;
  }, [tip]);

  if (!tip && !menu) return null;

  return (
    <>
      {tip
        ? createPortal(
            <div
              ref={tipElRef}
              className="openable-value-tip"
              data-var-state={tip.state || undefined}
              data-kind={tip.kind || undefined}
              style={{
                left: tip.x,
                top: tip.y,
                ...(tip.color ? { '--tip-color': tip.color } : null),
              }}
              role="tooltip"
            >
              <div className="openable-value-tip__text">{tip.label}</div>
              <div className="openable-value-tip__hint">{tip.feedback || tip.hint}</div>
            </div>,
            document.body
          )
        : null}
      {menu
        ? createPortal(
            <div
              ref={menuElRef}
              className="openable-value-menu"
              style={{ left: menu.x, top: menu.y }}
              role="menu"
              onPointerDown={(e) => e.preventDefault()}
            >
              <button
                type="button"
                className="openable-value-menu__item"
                role="menuitem"
                onPointerDown={handleCopy}
                onClick={handleCopy}
              >
                {menu.copied ? 'Copied!' : 'Copy'}
              </button>
            </div>,
            document.body
          )
        : null}
    </>
  );
};

/** Isolated from the app shell so opening or copying does not re-render it. */
export function OpenableValueLayer() {
  return useOpenableHoverTip();
}
